import { faSlidersH } from '@fortawesome/pro-solid-svg-icons';
import ExportPdfButton from 'components/Export/ExportPdfButton';
import IconButton from 'components/ui/Button/IconButton';
import FilterBox from 'components/ui/FilterBox';
import Flex from 'components/ui/Flex';
import Spinner from 'components/ui/Spinner';
import Text from 'components/ui/Text';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useHeaderScroll from 'hooks/useHeaderScroll';
import { useTranslation } from 'react-i18next';
import {
    clearFilters,
    searchPhraseClear,
    selectFlattenCompleteActiveFilters,
    selectSearchParams,
    showFilterToggled,
    toggleFilter,
} from 'store/reducers/assortmentSlice';
import styled from 'styled-components';

const ProductListHeader = ({ enableFilters }: { enableFilters: boolean | undefined }) => {
    const { loading, assortmentPageInfo, filters, filtersShow, categoriesRaw, products } = useAppSelector(
        ({ assortment }) => assortment,
    );
    const productSearchParams = useAppSelector(selectSearchParams);
    const totalProducts = assortmentPageInfo?.totalItems ?? null;
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const navVisible = useHeaderScroll();

    return (
        <ProductListHeaderWrapper stuck={navVisible}>
            <Flex align="center" justify="between" $wrap>
                {((enableFilters && filters.flatMap((fo) => fo.filter).length > 0) ||
                    (enableFilters && !!categoriesRaw?.length)) && (
                    <IconButton
                        large
                        bgColor="transparent"
                        onClick={() => dispatch(showFilterToggled())}
                        className="pointer f1-400"
                        icon={faSlidersH}
                        square
                    >
                        {filtersShow ? t('hide', 'Hide') : t('show')} {t('filters').toLowerCase()}
                    </IconButton>
                )}
                <ActionsWrapper align="center" justify="end">
                    <ProductAmountText light className="me-2">
                        {loading ? (
                            <Spinner />
                        ) : (
                            totalProducts != null && `${totalProducts} ${t('products').toLowerCase()}`
                        )}
                    </ProductAmountText>
                    <ExportPdfButton
                        data={{
                            products,
                            settings: {
                                showImagePicture: true,
                            },
                            fileName: t('products'),
                        }}
                        productSearchParams={productSearchParams}
                    />
                </ActionsWrapper>
            </Flex>
            <FilterBoxes />
        </ProductListHeaderWrapper>
    );
};

const FilterBoxes = () => {
    const flatFilters = useAppSelector(selectFlattenCompleteActiveFilters);
    const searchPhrase = useAppSelector(({ assortment }) => assortment.searchPhrase);

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    if (flatFilters === null && !searchPhrase) return null;

    return (
        <FilterFlexBoxesWrapper>
            <Fade align="left" />
            <FilterBoxesWrapper>
                {flatFilters?.map((filter) => (
                    <FilterBox
                        key={filter.value.key}
                        text={filter.value.value}
                        onRemove={() => {
                            dispatch(toggleFilter(filter));
                        }}
                    />
                ))}
                {searchPhrase && (
                    <FilterBox
                        text={searchPhrase}
                        onRemove={() => {
                            dispatch(searchPhraseClear());
                        }}
                    />
                )}

                {flatFilters && flatFilters.length > 1 && (
                    <ClearFilterBox
                        className="f1-500"
                        text={`${t('action.clear', 'Clear')} ${t('filter.filters', 'Filters').toLowerCase()}`}
                        onRemove={() => {
                            dispatch(clearFilters());
                        }}
                    />
                )}
            </FilterBoxesWrapper>
            <Fade align="right" />
        </FilterFlexBoxesWrapper>
    );
};

const ProductListHeaderWrapper = styled.div<{ stuck?: boolean }>`
    position: sticky;
    top: -150px;
    transition: top 0.2s ease;
    background-color: ${({ theme }) => theme.colors.fill};
    padding-top: 4px;
    padding-bottom: 4px;
    margin: 1px;
    z-index: 5;

    ${({ stuck, theme }) =>
        stuck &&
        `
      top: 71px;
      transition-duration: 0.5s;

      ${theme.media.lg} {
        top: 87px;
      }
    `}
`;

const ActionsWrapper = styled(Flex)`
    flex-grow: 1;
    gap: 8px;
`;

const ProductAmountText = styled(Text)`
    @media screen and (max-width: 450px) {
        display: none;
    }
`;

const ClearFilterBox = styled(FilterBox)`
    margin-left: 12px;
`;

const FilterFlexBoxesWrapper = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillMedium};
    margin-top: 4px;
`;

const FilterBoxesWrapper = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    overflow: auto;
    white-space: nowrap;
    max-width: 100%;
    padding: 0 15px;
    padding: 2px 15px 2px 15px;
    margin: 0 -15px 0 -15px;
`;

const Fade = styled.div<{ align: 'left' | 'right' }>`
    position: relative;
    height: 35px;
    background: ${({ theme, align }) =>
        `linear-gradient(to ${align}, rgba(255, 255, 255, 0), ${theme.colors.fillMedium})`};
    min-width: 15px;
    width: 15px;
    z-index: 1;
`;

export default ProductListHeader;
